/**
 * Control axes: expo curves, deadzones, and rate-limited keyboard shaping.
 *
 * ## Why a keyboard axis needs shaping at all
 *
 * A key is a switch — it is either fully deflected or not at all. Feeding that straight into a
 * flight model gives an aeroplane that can only be flown in full-scale inputs, which at 120 Hz
 * is violent. So a keyboard axis is *integrated*: holding a key ramps the axis toward its
 * limit at a fixed rate, and releasing it springs back toward centre. The ramp and return
 * rates are what make keyboard flying feel like a control rather than a switch.
 *
 * ## What the milestone-1 test flight found, and what changed
 *
 * A single constant ramp rate is not enough: it makes the axis leave centre at the same speed
 * it crosses the middle of its travel, so the shortest key tap a human can produce (~50–70 ms)
 * still commands a visible chunk of elevator, and hand-flying turns into a series of
 * over-corrections. Three changes fix the feel:
 *
 *   1. **A fine zone around neutral.** Inside {@link KeyboardAxisOptions.fineZone} the ramp
 *      rate is scaled down to {@link KeyboardAxisOptions.fineRateScale}, so a tap is a nudge
 *      and a hold still reaches the stop in about half a second. This is the single change
 *      that makes small corrections possible.
 *   2. **An exponential return to centre**, not a linear one. `moveTowards` releases at a
 *      constant rate and arrives at neutral with a corner in the velocity, which reads as the
 *      control snapping back. An exponential release with a small linear floor (so it actually
 *      *reaches* zero rather than approaching it forever) feels like a sprung stick.
 *   3. **A reversal boost.** When the pilot presses the opposite key the axis is being
 *      corrected, not explored, and crawling back through the fine zone would feel like the
 *      controls were sticking. Driving against the current deflection gets a faster rate.
 *
 * ## Expo
 *
 * `output = (1 − e)·x + e·x³` keeps full authority at the stops while flattening the response
 * around neutral, so small corrections are fine-grained. `e = 0` is linear. Cubic rather than
 * a power curve because it is odd — the response is exactly symmetric about zero, with no sign
 * handling to get wrong — and it is monotonic for every `e` in [0, 1].
 *
 * Expo and the fine zone are not the same tool, and both are needed: expo shapes *where the
 * axis already is*, the fine zone shapes *how fast it gets there*. Expo alone cannot help a
 * tap that has already integrated too far.
 *
 * `updateFixed(dt)`: {@link KeyboardAxis} integrates on the fixed step so keyboard response is
 * frame-rate independent.
 */

import { clamp, clamp01, expDamp, lerp, moveTowards } from '../core/math.js';

/** Apply an expo curve to a normalised axis value. `expo` 0 = linear, 1 = fully cubic. */
export function applyExpo(value: number, expo: number): number {
  const x = clamp(value, -1, 1);
  const e = clamp(expo, 0, 1);
  return (1 - e) * x + e * x * x * x;
}

/**
 * Remove a deadzone and rescale so the usable travel still reaches ±1.
 *
 * Without the rescale, a 10 % deadzone costs 10 % of the control authority — the stick would
 * hit its mechanical stop while the aircraft was still 10 % short of full deflection.
 */
export function applyDeadzone(value: number, deadzone: number): number {
  const dead = clamp(deadzone, 0, 0.95);
  const magnitude = Math.abs(value);
  if (magnitude <= dead) return 0;
  const scaled = (magnitude - dead) / (1 - dead);
  return Math.sign(value) * clamp(scaled, 0, 1);
}

export interface AxisShapingOptions {
  /** Expo, 0…1. */
  expo?: number;
  /** Deadzone as a fraction of full travel, 0…1. */
  deadzone?: number;
  /** Invert the axis. */
  invert?: boolean;
  /** Scale applied after shaping, e.g. 0.5 for a gentler control. */
  sensitivity?: number;
}

/** Shape a raw analogue reading into a control command. */
export function shapeAxis(raw: number, options: AxisShapingOptions = {}): number {
  const deadzoned = applyDeadzone(raw, options.deadzone ?? 0);
  const expo = applyExpo(deadzoned, options.expo ?? 0);
  const scaled = expo * (options.sensitivity ?? 1);
  return clamp(options.invert === true ? -scaled : scaled, -1, 1);
}

export interface KeyboardAxisOptions extends AxisShapingOptions {
  /** How fast holding a key drives the axis to its stop, outside the fine zone [units/s]. */
  rampRatePerS?: number;
  /**
   * Half-life of the spring back to centre [s]. Smaller is snappier; 0 releases instantly.
   * Frame-rate independent — see {@link expDamp}.
   */
  returnHalfLifeS?: number;
  /**
   * Floor on the return speed [units per second]. An exponential decay only *approaches*
   * zero; this is what makes the control actually arrive at neutral, and in finite time.
   */
  minReturnRatePerS?: number;
  /**
   * Travel either side of neutral over which the ramp rate is reduced, for fine control.
   * 0 disables the fine zone — which is what throttle and trim want, since for them "near
   * zero" is a perfectly ordinary place to be rather than the neutral point of a spring.
   */
  fineZone?: number;
  /** Ramp-rate multiplier at dead centre, blending to 1 at the edge of the fine zone. */
  fineRateScale?: number;
  /** Ramp-rate multiplier while driving against the current deflection. */
  reversalBoost?: number;
  /** Does the axis self-centre? Throttle and trim do not. */
  selfCentring?: boolean;
  /**
   * Discrete step applied the instant a key goes down, before any ramping.
   *
   * For a lever, "tap to nudge, hold to sweep" is the behaviour a physical detent gives you and
   * an integrated axis does not: with ramping alone the size of a tap is however long your
   * finger happened to be on the key, so setting exactly 40 % power is a game of chance. A tap
   * is therefore a fixed step, and ramping only begins once the key has been held past
   * {@link tapWindowS} — so the two behaviours never add up and a tap is exactly one step.
   */
  tapStep?: number;
  /** How long a key must be held before ramping takes over from the tap step [s]. */
  tapWindowS?: number;
  /** Starting value. */
  initialValue?: number;
  /** Lower limit. */
  min?: number;
  /** Upper limit. */
  max?: number;
}

/**
 * A control axis driven by two keys.
 *
 * `updateFixed(dt)`: yes. Allocation-free.
 */
export class KeyboardAxis {
  private value: number;
  private readonly rampRatePerS: number;
  private readonly returnHalfLifeS: number;
  private readonly minReturnRatePerS: number;
  private readonly fineZone: number;
  private readonly fineRateScale: number;
  private readonly reversalBoost: number;
  private readonly selfCentring: boolean;
  private readonly min: number;
  private readonly max: number;
  private readonly shaping: AxisShapingOptions;
  private readonly tapStep: number;
  private readonly tapWindowS: number;

  /** Which way the pilot was pushing last tick, so a key-down edge can be detected. */
  private lastDirection = 0;
  /** How long the current key has been held [s]. */
  private heldForS = 0;

  constructor(options: KeyboardAxisOptions = {}) {
    this.rampRatePerS = options.rampRatePerS ?? 2.2;
    this.returnHalfLifeS = options.returnHalfLifeS ?? 0.09;
    this.minReturnRatePerS = options.minReturnRatePerS ?? 0.9;
    this.fineZone = Math.max(0, options.fineZone ?? 0);
    this.fineRateScale = clamp01(options.fineRateScale ?? 1);
    this.reversalBoost = Math.max(1, options.reversalBoost ?? 1);
    this.selfCentring = options.selfCentring ?? true;
    this.tapStep = Math.max(0, options.tapStep ?? 0);
    this.tapWindowS = Math.max(0, options.tapWindowS ?? 0.12);
    this.min = options.min ?? -1;
    this.max = options.max ?? 1;
    this.value = clamp(options.initialValue ?? 0, this.min, this.max);
    this.shaping = {
      expo: options.expo ?? 0,
      deadzone: options.deadzone ?? 0,
      invert: options.invert ?? false,
      sensitivity: options.sensitivity ?? 1,
    };
  }

  /**
   * Ramp rate for this tick [units/s], given which way the pilot is pushing.
   *
   * Slow near neutral so a tap is a nudge, full rate once clear of the fine zone, and boosted
   * when the input opposes the current deflection so corrections are not sluggish.
   */
  private rampRate(direction: number): number {
    const magnitude = Math.abs(this.value);
    const fine = this.fineZone > 0 ? clamp01(magnitude / this.fineZone) : 1;
    let rate = this.rampRatePerS * lerp(this.fineRateScale, 1, fine);
    if (this.value !== 0 && Math.sign(this.value) !== direction) rate *= this.reversalBoost;
    return rate;
  }

  /**
   * Advance the axis. `positive` and `negative` are the two key states.
   * Returns the shaped command.
   */
  updateFixed(positive: boolean, negative: boolean, dtSeconds: number): number {
    const direction = (positive ? 1 : 0) - (negative ? 1 : 0);
    const pressed = direction !== 0 && direction !== this.lastDirection;
    if (pressed) this.heldForS = 0;
    else if (direction !== 0) this.heldForS += dtSeconds;
    this.lastDirection = direction;

    if (direction !== 0) {
      if (pressed && this.tapStep > 0) {
        // The tap. Ramping is suppressed until the key has been held past the window, so a tap
        // is exactly one step and a hold is a smooth sweep — never a step plus a partial ramp.
        this.value = clamp(this.value + direction * this.tapStep, this.min, this.max);
      } else if (this.tapStep === 0 || this.heldForS >= this.tapWindowS) {
        const step = direction * this.rampRate(direction) * dtSeconds;
        this.value = clamp(this.value + step, this.min, this.max);
      }
    } else if (this.selfCentring) {
      // Exponential release, then a linear floor so the axis lands exactly on neutral instead
      // of asymptotically hovering a thousandth off it forever.
      const eased = expDamp(this.value, 0, this.returnHalfLifeS, dtSeconds);
      this.value = moveTowards(eased, 0, this.minReturnRatePerS * dtSeconds);
    }

    return shapeAxis(this.value, this.shaping);
  }

  /** The shaped command for the current position, without advancing anything. */
  get command(): number {
    return shapeAxis(this.value, this.shaping);
  }

  /** Raw, unshaped position. */
  get raw(): number {
    return this.value;
  }

  /** Set the raw position directly, e.g. when a gamepad takes over. */
  set(value: number): void {
    this.value = clamp(value, this.min, this.max);
  }

  /** Nudge the axis by a fixed amount — trim wheels and flap detents work this way. */
  nudge(delta: number): void {
    this.value = clamp(this.value + delta, this.min, this.max);
  }

  reset(): void {
    this.value = 0;
    this.lastDirection = 0;
    this.heldForS = 0;
  }
}
